import React, { createRef, useContext, useState } from 'react';
import { Box, Column, Row, useMediaQueries } from '@honda-canada/design-system-react';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import { useTranslation } from 'react-i18next';

import Context from './service/Context';
import themeStyles, {
  AccordionsCopy,
  AccordionsDisclaimersCopy,
  AccordionsItemTitle,
} from './TrimSpecifications.styles';

const StyledBox = themeStyles.apply(Box, 'AccordionsBox');
const StyledPrintBox = themeStyles.apply(Box, 'AccordionsPrintBox');
const AccordionsContentRow = themeStyles.apply(Row, 'AccordionsContentRow');
const AccordionsContentColumn = themeStyles.apply(Column, 'AccordionsContentColumn');
const AccordionsContentBox = themeStyles.apply(Box, 'AccordionsContentBox');
const StyledSplide = themeStyles.apply(SplideSlide, 'AccordionsSplide');

export const getItemClassName = (itemValueClass, specIndex, index, itemParentGroup) => {
  if (!itemValueClass || !itemParentGroup) {
    return '';
  }
  return `${itemValueClass}-${specIndex}-${index}-${itemParentGroup.replace(/\s/g, '')}`;
};

/**
 *
 *
 * @param {string[]} itemHeights
 * @param {number} specIndex
 * @param {number} index
 */
export const getItemHeight = (itemHeights, specIndex, index) => itemHeights?.[specIndex]?.[index];

const useGetAccordionItems = ({ refs, onDrag, specifications, itemParentGroup, printItemsList }) => {
  const [contentHeights, setContentHeights] = useState([[]]);

  const { isSmallDesktop, isDesktop } = useMediaQueries();
  const { t } = useTranslation();

  const configurationProvider = useContext(Context);
  const { isDark, selectedTrim, currentSlide, splideOptions } = configurationProvider || {};

  const categories = specifications?.filter(({ name }) => name !== 'legal_disclaimers_');
  const legalDisclaimers = specifications?.find(({ name }) => name === 'legal_disclaimers_');
  const isMobile = !(isSmallDesktop || isDesktop);
  const itemLabelClass = 'spec-item-label';
  const itemValueClass = 'spec-item-value';
  // eslint-disable-next-line no-param-reassign
  refs.current = categories?.map((_, i) => refs.current[i] || createRef());

  const accordionItems = categories.map((category, specIndex) => ({
    key: category.name,
    expandAriaLabel: `${category.label} ${t('Shared.Common.expandAria')}`,
    collapseAriaLabel: `${category.label} ${t('Shared.Common.collapseAria')}`,
    title: (
      <AccordionsItemTitle
        size="accordionLarge"
        data-testid="cy-product-specification"
        isDark={isDark}
        dangerouslySetInnerHTML={{ __html: category.label }}
      />
    ),
    content: (
      <AccordionsContentRow isDark={isDark}>
        <AccordionsContentColumn width={[1 / 2, 1 / 6]}>
          {category?.specificationLabels?.map((item, i) => (
            <StyledBox
              height={getItemHeight(contentHeights, specIndex, i)}
              className={getItemClassName(itemLabelClass, specIndex, i, itemParentGroup)}
              key={item.label}
            >
              <AccordionsCopy isDark={isDark} size="small" dangerouslySetInnerHTML={{ __html: item.label }} />
            </StyledBox>
          ))}
        </AccordionsContentColumn>
        <AccordionsContentColumn width={[1 / 2, 5 / 6]}>
          <Splide
            options={{
              ...splideOptions,
              slideFocus: false,
            }}
            ref={refs?.current[specIndex]}
            onActive={onDrag}
          >
            {category?.specifications?.map((specification, index) => {
              const selected = isMobile ? currentSlide === index : selectedTrim === specification.itemId?.toString();
              return (
                <StyledSplide key={specification.itemId}>
                  <AccordionsContentBox selected={selected} isDark={isDark}>
                    {category?.specificationLabels?.map((label, i) => (
                      <StyledBox
                        key={`${specification.itemId}-${label.label}`}
                        height={getItemHeight(contentHeights, specIndex, i)}
                        className={getItemClassName(itemValueClass, specIndex, i, itemParentGroup)}
                        textAlign="left"
                      >
                        <AccordionsCopy
                          size="small"
                          textAlign="center"
                          isDark={isDark}
                          isSingleColumn={category.specifications?.length === 1}
                          dangerouslySetInnerHTML={{ __html: specification.specs?.[i]?.value }}
                        />
                      </StyledBox>
                    ))}
                  </AccordionsContentBox>
                </StyledSplide>
              );
            })}
          </Splide>
        </AccordionsContentColumn>
      </AccordionsContentRow>
    ),
  }));

  const printAccordionItems = categories.map((category, specIndex) => ({
    key: category.name,
    expandAriaLabel: `${category.label} ${t('Shared.Common.expandAria')}`,
    collapseAriaLabel: `${category.label} ${t('Shared.Common.collapseAria')}`,
    title: <AccordionsItemTitle size="accordionLarge" dangerouslySetInnerHTML={{ __html: category.label }} />,
    content: (
      <AccordionsContentRow>
        <AccordionsContentColumn width={[1 / 4]}>
          <StyledPrintBox height={90}>
            <AccordionsCopy size="extraSmall" dangerouslySetInnerHTML={{ __html: '' }} />
          </StyledPrintBox>
          {category?.specificationLabels?.map((item, i) => (
            <StyledPrintBox
              height={getItemHeight(contentHeights, specIndex, i)}
              className={getItemClassName(itemLabelClass, specIndex, i, itemParentGroup)}
              key={item.label}
            >
              <AccordionsCopy size="extraSmall" dangerouslySetInnerHTML={{ __html: item.label }} />
            </StyledPrintBox>
          ))}
        </AccordionsContentColumn>
        <AccordionsContentColumn display="flex" justifyContent="space-around" width={[3 / 4]}>
          {category?.specifications?.map((specification, ind) => (
            <AccordionsContentBox key={specification.itemId}>
              <StyledPrintBox height={90} textAlign="center">
                {printItemsList?.[ind]}
              </StyledPrintBox>
              {category?.specificationLabels?.map((label, i) => (
                <StyledPrintBox
                  key={`${specification.itemId}-${label.label}`}
                  height={getItemHeight(contentHeights, specIndex, i)}
                  className={getItemClassName(itemValueClass, specIndex, i, itemParentGroup)}
                  textAlign="left"
                >
                  <AccordionsCopy
                    size="extraSmall"
                    textAlign="center"
                    isSingleColumn={category.specifications?.length === 1}
                    dangerouslySetInnerHTML={{ __html: specification.specs?.[i]?.value }}
                  />
                </StyledPrintBox>
              ))}
            </AccordionsContentBox>
          ))}
        </AccordionsContentColumn>
      </AccordionsContentRow>
    ),
  }));

  const legalDisclaimersAccordionItem = legalDisclaimers && {
    key: 'legal_disclaimers_',
    title: (
      <AccordionsDisclaimersCopy size="accordionLarge" isDark={isDark}>
        {legalDisclaimers.label}
      </AccordionsDisclaimersCopy>
    ),
    content: legalDisclaimers?.specificationLabels?.map(disclaimer => (
      <Row backgroundColor={isDark ? 'black' : 'white'} key={disclaimer.label}>
        <Column width={1} pl="0">
          <StyledBox>
            <AccordionsCopy
              color={isDark ? 'white' : 'default'}
              isDark={isDark}
              size="small"
              dangerouslySetInnerHTML={{ __html: disclaimer?.label }}
            />
          </StyledBox>
        </Column>
      </Row>
    )),
  };

  const allAccordionItems = legalDisclaimersAccordionItem
    ? [...accordionItems, legalDisclaimersAccordionItem]
    : accordionItems;

  const allPrintAccordionItems = legalDisclaimersAccordionItem
    ? [...printAccordionItems, legalDisclaimersAccordionItem]
    : printAccordionItems;

  const printGroupKeys = allPrintAccordionItems.map(({ key }) => key);

  return { allAccordionItems, allPrintAccordionItems, printGroupKeys, setContentHeights };
};

export default useGetAccordionItems;
